import { ComponentProps, FC, isValidElement } from 'react';
import { Button, CloseIcon, ConfirmationDialog, YellowWarningIcon } from '@repo/atomic-components';

type DialogPt = ComponentProps<typeof ConfirmationDialog>['pt'];

interface WarningConfirmationDialogProps {
  /** The dialog id passed to `confirmDialog({ id })`. */
  id: string;
  /** Body copy shown under the yellow-triangle "Are you sure?" header.
   *  A string or element `bodyText` passed to `confirmDialog({ id, bodyText })`
   *  overrides it for that one prompt (e.g. copy with a live count). */
  bodyText: string;
  /** Label on the red destructive button (e.g. "Delete", "Abandon"). */
  confirmLabel: string;
  /** Optional PT overrides forwarded to the underlying ConfirmationDialog
   *  — useful for mask z-index overrides when the dialog needs to sit
   *  above an app-specific overlay layer. */
  pt?: DialogPt;
}

/**
 * Reusable 280px-wide warning dialog used for destructive confirms.
 * Renders the yellow-triangle "Are you sure?" header, free-text body
 * copy, and a Cancel / red-confirm button bar. Visuals match the
 * existing MR-page delete and case-info-sidebar abandon dialogs.
 */
export const WarningConfirmationDialog: FC<WarningConfirmationDialogProps> = ({ id, bodyText, confirmLabel, pt }) => (
  <ConfirmationDialog
    id={id}
    style={{ width: '280px', borderRadius: '8px', border: '1px solid #e1e1e0', overflow: 'hidden' }}
    contentClassName="!p-0"
    headerClassName="!hidden"
    pt={pt}
  >
    {(params, { accept, reject }) => (
      <div className="relative p-[24px]">
        <button
          type="button"
          aria-label="Close"
          onClick={reject}
          className="absolute top-[24px] right-[24px] cursor-pointer rounded p-1 focus:outline-none"
        >
          <CloseIcon width={12} height={12} />
        </button>
        <div className="mt-[16px] flex flex-col items-center">
          <YellowWarningIcon width={48} height={48} />
          <div className="text-fletchers-black-900 mt-[16px] text-[18px]/[18px] font-[600]">Are you sure?</div>
        </div>
        <div className="text-fletchers-black-900 mt-[24px] text-center text-[14px]/[20px]">
          {typeof params.bodyText === 'string' || isValidElement(params.bodyText) ? params.bodyText : bodyText}
        </div>
        <div className="mt-[24px] flex gap-[8px]">
          <Button
            outlined
            label="Cancel"
            className="hover:bg-fletchers-gray-110 flex-1"
            onClick={reject}
            style={{ height: '32px', fontSize: '14px', borderRadius: '4px' }}
          />
          <Button
            label={confirmLabel}
            className="flex-1 [&:hover]:!bg-[#C42E2E]"
            onClick={accept}
            style={{
              height: '32px',
              fontSize: '14px',
              borderRadius: '4px',
              backgroundColor: '#E13C3C',
              borderColor: '#E13C3C',
              color: 'white',
            }}
          />
        </div>
      </div>
    )}
  </ConfirmationDialog>
);
